import { useState } from "react";
import { ImageUploader } from "./ImageUploader";
import { uploadFile } from "../utils/upload";
import { AxiosError } from "axios";
import { IErrorData, UploadTicketType } from "@/common/services/types";
import { retryAfter } from "@/components/uploader/utils/retryAfter";

interface IImageUploaderContainerProps {
  preview?: string;
  title: string;
  onReady: (ticketId: string) => void;
  onError: (error: string) => void;
  onProgress?: (percent: number) => void;
  onStart?: () => void;
  id?: string;
  uploadTicketType: UploadTicketType;
  minImgWidth?: number;
  minImgHeight?: number;
}

export const ImageUploaderContainer: React.FC<IImageUploaderContainerProps> = (props) => {
  const { preview, title, onReady, onError, onProgress, onStart, uploadTicketType, minImgWidth, minImgHeight, id } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [isError, setError] = useState(false);

  const onFileUpload = async (file: File) => {
    onStart && onStart();

    try {
      setLoading(true);
      setError(false);
      const { id } = await uploadFile(
        file,
        uploadTicketType,
        (sent: number) => {
          const MULTIPLIFIER = 100;
          const percent = Math.round((sent * MULTIPLIFIER) / file.size);
          onProgress && onProgress(percent);
        }
      );

      onReady(id);
      onProgress && onProgress(0);
    } catch (err) {
      const error = err as AxiosError<IErrorData>;
      let message = error.response?.data?.error?.message || error.message;
      const code = error.response?.data?.error?.code;

      if (code === "UploadRateLimit") {
        const delay = error.response?.data?.error?.delay || 0;
        const tryAfterMessage = delay > 0 ? `please try again after ${retryAfter(delay)}` : "please try again later";
        message = `Daily upload limit reached, ${tryAfterMessage}`;
      }

      onError(message);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ImageUploader
      preview={preview}
      isError={isError}
      loading={loading}
      title={title}
      id={id}
      minImgWidth={minImgWidth}
      minImgHeight={minImgHeight}
      onFileUpload={onFileUpload}
    />
  );
};
